import React, { useState, useEffect } from 'react';
import { colors } from '../styles/styles';
import CreateCollectionModal from './CreateCollectionModal';
import { getCollections, ensureDefaultCollection, deleteCollection } from '../services/collectionService';
import { useSwipeable } from 'react-swipeable';
import PageHeader from './PageHeader';
import StaggeredAnimation from './common/StaggeredAnimation';
import PlusIcon from './common/PlusIcon';
import IconButton from './common/IconButton';
import { formatTagName } from '../utils/formatters';

/**
 * Library component that displays a list of thought collections
 */
const Library = ({ navigateToCollection }) => {
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [authError, setAuthError] = useState(false);
    const [tagsExpanded, setTagsExpanded] = useState(false);
    
    // Fetch collections on component mount
    useEffect(() => {
        fetchCollections();
    }, []);
    
    // Function to fetch collections (can be called after creating a new collection)
    const fetchCollections = async () => {
        setLoading(true);
        setAuthError(false);
        
        try {
            // Ensure the default collection exists
            await ensureDefaultCollection();
            
            // Fetch all collections
            const fetchedCollections = await getCollections();
            setCollections(fetchedCollections);
        } catch (error) {
            console.error('Error fetching collections:', error);
            // Check if this is an authentication error
            if (error.message && error.message.includes('not authenticated')) {
                setAuthError(true);
            }
            // Handle the error appropriately - at minimum show an empty state
            setCollections([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle showing the create collection modal
    const handleCreateClick = () => {
        setShowCreateModal(true);
    };
    
    // Handle collection creation success
    const handleCollectionCreated = () => {
        fetchCollections(); // Refresh the collections
    };

    // Handle clicking on a collection with fade out animation
    const handleCollectionClick = (collection) => {
        // Simply call the navigation function from props - AppContent will handle the animation
        navigateToCollection(collection.id, collection.name, collection.isDefault, collection.isTagCollection);
    };

    // Handle deleting a collection
    const handleDeleteCollection = async (collectionId) => {
        try {
            await deleteCollection(collectionId);
            // Update the local state to remove the deleted collection
            setCollections(prevCollections => 
                prevCollections.filter(collection => collection.id !== collectionId)
            );
        } catch (error) {
            console.error('Error deleting collection:', error);
            alert('Failed to delete collection. Please try again.');
        }
    };

    // Sort collections to ensure "Your Thoughts" is always at the top
    // then non-tag collections alphabetically, then tag collections alphabetically
    const getSortedCollections = () => {
        if (!collections.length) return [];
        
        // Separate default collection, regular collections, and tag collections
        const defaultCollection = collections.find(c => c.isDefault);
        const tagCollections = collections.filter(c => c.isTagCollection);
        const regularCollections = collections.filter(c => !c.isDefault && !c.isTagCollection);
        
        // Sort regular collections alphabetically by name
        const sortedRegularCollections = regularCollections.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
        
        // Sort tag collections alphabetically by name
        const sortedTagCollections = tagCollections.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
        
        // Return with default collection at the top, followed by regular collections, then tag collections
        return [
            ...(defaultCollection ? [defaultCollection] : []),
            ...sortedRegularCollections,
            ...sortedTagCollections
        ];
    };

    // Add an authentication error state
    if (authError) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0', color: colors.textSecondary }}>
                <p>You need to be signed in to access collections.</p>
                <button
                    onClick={() => window.location.reload()} // Simple reload to trigger auth
                    style={{
                        backgroundColor: colors.accent.white,
                        color: 'black',
                        border: 'none',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        marginTop: '16px',
                        cursor: 'pointer'
                    }}
                >
                    Sign In
                </button>
            </div>
        );
    }

    // Get sorted collections
    const sortedCollections = getSortedCollections();

    // Separate collections into groups
    const defaultCollection = sortedCollections.find(c => c.isDefault);
    const regularCollections = sortedCollections.filter(c => !c.isDefault && !c.isTagCollection);
    const tagCollections = sortedCollections.filter(c => c.isTagCollection);

    return (
        <div className="library-container smooth-scroll" style={{ width: '100%' }}>
            {/* Remove all animation keyframes since we're using the one from AppContent */}
            <StaggeredAnimation containerClass="collections-feed" />
            
            <PageHeader 
                title={{ text: "Library", large: true }}
                rightContent={
                    <IconButton
                        onClick={handleCreateClick}
                        icon={<PlusIcon strokeLinecap="butt" strokeLinejoin="miter" />}
                        ariaLabel="Create collection"
                    />
                }
            />
            
            {/* Render collections or empty state */}
            {!loading && collections.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: colors.textSecondary }}>
                    No collections found. Create your first collection.
                </div>
            ) : !loading && (
                <div className="collections-feed" style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    width: '100%',
                }}>
                    {/* Default and Regular Collections */}
                    {defaultCollection && (
                        <CollectionItem 
                            key={defaultCollection.id}
                            collection={defaultCollection}
                            isLast={regularCollections.length === 0}
                            onClick={() => handleCollectionClick(defaultCollection)}
                            onDelete={null}
                        />
                    )}
                    
                    {regularCollections.length > 0 && (
                        <>
                            {regularCollections.map((collection, index) => (
                                <CollectionItem 
                                    key={collection.id}
                                    collection={collection}
                                    isLast={index === regularCollections.length - 1}
                                    onClick={() => handleCollectionClick(collection)}
                                    onDelete={() => handleDeleteCollection(collection.id)}
                                />
                            ))}
                        </>
                    )}
                </div>
            )}
            
            {/* Tag Collections Section - Separate from collections-feed */}
            {tagCollections.length > 0 && (
                <div className="tags-section">
                    <div 
                        onClick={() => setTagsExpanded(!tagsExpanded)}
                        style={{
                            padding: '16px',
                            fontSize: '13px',
                            fontWeight: '500',
                            color: colors.textSecondary,
                            borderBottom: `1px solid ${colors.divider}`,
                            marginTop: '24px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <span>TAGS</span>
                        <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                                transform: tagsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease'
                            }}
                        >
                            <path 
                                d="M9 18L15 12L9 6" 
                                stroke={colors.textSecondary} 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    {tagsExpanded && (
                        <div style={{ 
                            opacity: 1
                        }}>
                            {tagCollections.map((collection, index) => (
                                <CollectionItem 
                                    key={collection.id}
                                    collection={collection}
                                    isLast={index === tagCollections.length - 1}
                                    onClick={() => handleCollectionClick(collection)}
                                    onDelete={() => handleDeleteCollection(collection.id)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
            
            {/* Create Collection Modal */}
            <CreateCollectionModal 
                isVisible={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handleCollectionCreated}
            />
        </div>
    );
};

/**
 * CollectionItem component with swipe functionality
 */
const CollectionItem = ({ collection, isLast, onClick, onDelete }) => {
    const [swipeState, setSwipeState] = useState({ isOpen: false, direction: null });
    const [isDeleting, setIsDeleting] = useState(false);
    
    const handleDelete = async (e) => {
        e.stopPropagation();
        if (!onDelete) return; // Do nothing if no delete handler provided
        
        setIsDeleting(true);
        try {
            await onDelete();
        } finally {
            setIsDeleting(false);
            setSwipeState({ isOpen: false, direction: null });
        }
    };
    
    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => onDelete && setSwipeState({ isOpen: true, direction: 'left' }),
        onSwipedRight: () => setSwipeState({ isOpen: false, direction: 'right' }),
        preventDefaultTouchmoveEvent: true,
        trackMouse: true,
        delta: 10,
        onSwiping: (eventData) => {
            if (Math.abs(eventData.deltaX) > Math.abs(eventData.deltaY)) {
                eventData.event.preventDefault();
                eventData.event.stopPropagation();
            }
        }
    });
    
    const handleTouchStart = (e) => {
        if (!onDelete) return; // Skip if not deletable
        
        const initialTouch = e.touches[0];
        const touchStartX = initialTouch.clientX;
        const touchStartY = initialTouch.clientY;
        
        const handleTouchMove = (e) => {
            if (!e.touches.length) return;
            
            const touch = e.touches[0];
            const deltaX = touch.clientX - touchStartX;
            const deltaY = touch.clientY - touchStartY;
            
            if (Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
                e.preventDefault();
            }
        };
        
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        
        const cleanup = () => {
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', cleanup);
            document.removeEventListener('touchcancel', cleanup);
        };
        
        document.addEventListener('touchend', cleanup, { once: true });
        document.addEventListener('touchcancel', cleanup, { once: true });
    };
    
    // Format collection name if it's a tag collection
    const displayName = collection.isTagCollection ? formatTagName(collection.name) : collection.name;
    
    return (
        <div 
            style={{ 
                position: 'relative', 
                overflow: 'hidden',
                willChange: 'transform, opacity',
            }}
            className="collection-item"
        >
            {/* Delete button (revealed when swiped left) */}
            {onDelete && (
                <div 
                    onClick={handleDelete}
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '80px',
                        backgroundColor: '#FF3B30',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer',
                    }}
                >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                </div>
            )}
            
            {/* Main content */}
            <div 
                {...(onDelete ? swipeHandlers : {})}
                onTouchStart={onDelete ? handleTouchStart : undefined}
                onClick={onClick}
                style={{ 
                    padding: '16px',
                    cursor: 'pointer',
                    backgroundColor: colors.background,
                    borderBottom: isLast ? 'none' : `1px solid ${colors.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    transform: swipeState.isOpen ? 'translateX(-80px)' : 'translateX(0)',
                    transition: 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)',
                    touchAction: 'pan-y',
                }}
            >
                {/* Remove Identicon */}
                
                <span style={{
                    fontSize: '15px',
                    lineHeight: '1.5',
                    color: colors.textPrimary,
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                }}>
                    {displayName}
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>
        </div>
    );
};

export default Library; 